import React, { FC, MouseEvent, useCallback } from 'react';
import { addProtocol } from '../../../../utils/addProtocol';
import { makeTransition, TransitionType } from '../../../../utils/styles/makeTransition';
import { useTranslation } from '../../../../utils/useTranslation';
import { IconForward } from '../../../icon/IconForward';
import { IconNewTab } from '../../../icon/IconNewTab';
import { IconProduct } from '../../../icon/IconProduct';
import { Button } from '../../../input/Button';
import { Box } from '../../../layout/Box';
import { ProductView } from '../../PostCard/PostButton/ProductView';
import {
    MainButton,
    ProductContainer,
    ProductContent,
    ProductMinizeButton,
} from '../../PostCard/PostButton/styles';
import { PostUI } from '../../types';
import { PostDetailsCardProps, usePostDetailsCardContext } from '../PostDetailsCard';
import { makeMediaParams } from '../utils';

export interface PostButtonProps {
    post: PostUI;
    isProductExpanded: boolean;
    onClickOutClick: PostDetailsCardProps['onClickOutClick'];
    onProductButtonClick: PostDetailsCardProps['onProductButtonClick'];
}

const ANIMATE_TIME = 250;

export const PostButton: FC<PostButtonProps> = ({
    post,
    isProductExpanded,
    onClickOutClick,
    onProductButtonClick,
}) => {
    const { activeIndex } = usePostDetailsCardContext();
    const t = useTranslation();

    const products = post.products;
    const clickOut = (post.clickouts ?? []).find((c) => c.target === 'button' && c.url);

    const handleClickOutClick = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            onClickOutClick?.(makeMediaParams(post, activeIndex));
            event.stopPropagation();
        },
        [activeIndex, post, onClickOutClick],
    );

    if (products.items.length) {
        return (
            <MainButton onClick={onProductButtonClick}>
                <ProductMinizeButton
                    isProductExpanded={isProductExpanded}
                    transition={makeTransition({
                        ms: isProductExpanded ? 0 : ANIMATE_TIME,
                        styles: ['opacity'],
                        type: TransitionType.EASE_OUT,
                    })}
                >
                    <Box mr="10px">
                        <IconProduct size={20} />
                    </Box>
                    {t(products.buttonTitle)}
                </ProductMinizeButton>

                <ProductContainer
                    isProductExpanded={isProductExpanded}
                    transition={makeTransition({
                        ms: isProductExpanded ? ANIMATE_TIME : 0,
                        styles: ['opacity'],
                        type: TransitionType.EASE_OUT,
                    })}
                >
                    <ProductContent>
                        <ProductView products={products} />
                    </ProductContent>

                    <IconForward fontSize="16px" />
                </ProductContainer>
            </MainButton>
        );
    }

    if (clickOut) {
        return (
            <Button
                as="a"
                target="_blank"
                rel="nofollow noreferrer"
                flexGrow={1}
                href={addProtocol(clickOut.url)}
                color="hardLight"
                onClick={handleClickOutClick}
                width="100%"
                fontSize="1rem"
            >
                {clickOut.text}
                <Box ml={1}>
                    <IconNewTab fontSize={14} color="#000" />
                </Box>
            </Button>
        );
    }

    return null;
};
